import { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'
import { TICKET_LIST_COLUMNS } from '../services/ticketService'
import usePersistedState from './usePersistedState'

/**
 * Hook personalizado para gestionar tickets con capacidad de filtrado.
 * * @returns {Object} { tickets, loading, error, filter, setFilter, reload }
 */
export default function useTickets() {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /** * Estado del filtro: 'all' para todos, 'own' para los del usuario.
   * Se persiste en localStorage para recordar la preferencia del usuario.
   * @type {['all'|'own', Function]}
   */
  const [filter, setFilter] = usePersistedState('tickets.filter', 'own')

  // Extraemos supabase y el empleado actual del contexto. Ojo: los tickets
  // guardan en responsibleId el id de Empleados (employee.id), NO el auth id.
  const { supabase, employee } = useApp()

  /**
   * Obtiene los tickets filtrados según el estado actual
   */
  const getTicketsAsync = async () => {
    if (!supabase) {
      setError('No hay conexión con la base de datos')
      return
    }

    setLoading(true)
    setError(null)

    // En modo 'own' necesitamos el empleado para filtrar por responsibleId.
    // Si aún no se ha hidratado, no cargamos para no mostrar TODOS los tickets.
    if (filter === 'own' && !employee?.id) {
      setTickets([])
      setLoading(false)
      return
    }

    try {
      // 1. Iniciamos la base de la query
      let query = supabase
        .schema('Tickets')
        .from('ticket')
        .select(TICKET_LIST_COLUMNS)
        .eq('deleted', false) // Excluimos los tickets con borrado lógico
        .order('createdAt', { ascending: false }) // Opcional: ordenar por los más recientes

      // 2. Aplicamos filtro si es modo 'own': mostramos las asignaciones del
      // usuario (responsibleId = employee.id), no los tickets que él creó.
      if (filter === 'own' && employee?.id) {
        query = query.eq('responsibleId', employee.id)
      }

      const { data, error: dbError } = await query

      if (dbError) throw dbError

      setTickets(data || [])
    } catch (err) {
      console.error('Error al cargar tickets:', err)
      setError(err.message || 'Error inesperado al cargar tickets')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Recarga los tickets cuando cambia el filtro o la conexión
   */
  useEffect(() => {
    // Si el filtro cambia, queremos que cargue siempre,
    // por eso ignoramos el hasLoaded si es una actualización de filtro
    getTicketsAsync()
  }, [supabase, filter, employee?.id])

  return {
    tickets,
    loading,
    error,
    filter, // Exponemos el estado actual del filtro
    setFilter, // Función para cambiar entre 'all' y 'own'
    reload: getTicketsAsync,
  }
}
